// packages/agent-kit/src/hooks/investment/useUserPortfolio.ts
import useSWR from 'swr';
import { portfolioService, isApiError } from '@dopomogai/api-client';
import { AgentKitError } from '../../types';
import type { PortfolioResponse } from '@dopomogai/types';

// The fetcher function that SWR will call.
const portfolioFetcher = async (key: string) => {
  const response = await portfolioService.getMyPortfolio();
  if (isApiError(response)) {
    // Let SWR handle the error state by throwing.
    throw new AgentKitError('Failed to fetch user portfolio', response);
  }
  return response;
};

/**
 * A hook to fetch the authenticated user's complete investment portfolio.
 * Used for the "My Investments" dashboard.
 *
 * @example
 * const { portfolio, isLoading, error } = useUserPortfolio();
 * if (isLoading) return <p>Loading portfolio...</p>;
 * if (error) return <p>Error: {error.message}</p>;
 * return <PortfolioDisplay data={portfolio} />;
 */
export const useUserPortfolio = () => {
  // The key is a simple string representing the API endpoint.
  // SWR will cache the data against this key.
  const swrKey = '/my-portfolio';

  const { data, error, isLoading, mutate } = useSWR<PortfolioResponse, AgentKitError>(
    swrKey,
    portfolioFetcher,
    {
      revalidateOnFocus: false,
    }
  );

  // Backward-compat mapping: accept either current_market_value_credits (new BE)
  // or book_value_credits (old BE), preferring the new field.
  const mapped = data
    ? {
        ...data,
        holdings: data.holdings.map((h: any) => ({
          ...h,
          current_market_value_credits:
            h.current_market_value_credits ?? h.book_value_credits ?? 0,
        })),
      }
    : data;

  return {
    /** The user's portfolio data. Undefined if loading or an error occurred. */
    portfolio: mapped,
    /** A boolean indicating if the request is in flight. */
    isLoading,
    /** An error object if the fetch failed. */
    error,
    /** The SWR mutate function to manually trigger a re-fetch (e.g., after a successful investment). */
    mutate,
  };
};